/**
 * The IELTS course (D-105): the path (levels → units → lessons, locked / open / completed), one
 * lesson (objectives, explanation in English with the French help, vocabulary, exercises) and the
 * spaced review of the day. Shapes of apps/academy/course.py (`course_path`, `lesson_detail`).
 */

import { type Feedback, type Item, type ItemResponse, parseFeedback, parseItem, parseResponse } from "./items";
import { isObject, num, numOrNull, objects, str, strings } from "./read";

export type LessonState = "locked" | "unlocked" | "completed";
/** A text stored as {en, fr, …} (the course content is English, the help French). */
export type LocalizedText = Record<string, string>;

export function localizedText(value: unknown): LocalizedText {
  if (typeof value === "string") return { en: value };
  if (!isObject(value)) return {};
  const out: LocalizedText = {};
  for (const [key, text] of Object.entries(value)) if (typeof text === "string") out[key] = text;
  return out;
}

/** The text of a {en, fr} title in the interface language (English content as a fallback). */
export function pick(value: unknown, language: string): string {
  const text = localizedText(value);
  const lang = language.split("-")[0] ?? "en";
  return text[lang] || text.en || Object.values(text)[0] || "";
}

/** English first (the IELTS content is always in English). */
export function english(value: unknown): string {
  return pick(value, "en");
}

/** The French help of a title or a text, when there is one and it differs from the English. */
export function frenchHelp(value: unknown): string {
  const text = localizedText(value);
  return text.fr && text.fr !== text.en ? text.fr : "";
}

export interface PathLesson {
  id: string;
  code: string;
  title: LocalizedText;
  state: LessonState;
  score: number | null;
  exercises: number;
}

export interface PathUnit {
  id: string;
  code: string;
  title: LocalizedText;
  skills: string[];
  lessons: PathLesson[];
}

export interface PathLevel {
  id: string;
  level: string;
  title: LocalizedText;
  cefr: string;
  bandTarget: string;
  units: PathUnit[];
}

export interface CoursePath {
  startLevel: string;
  levels: PathLevel[];
  completed: number;
  total: number;
  nextLesson: string | null;
  xp: number;
  reviewsDue: number;
}

function lessonState(value: unknown): LessonState {
  return value === "completed" || value === "unlocked" ? value : "locked";
}

export function parseCoursePath(value: unknown): CoursePath {
  const data = isObject(value) ? value : {};
  const levels = objects(data.courses).map(
    (course): PathLevel => ({
      id: str(course.id),
      level: str(course.level),
      title: localizedText(course.title),
      cefr: str(course.cefr),
      bandTarget: str(course.band_target),
      units: objects(course.units).map(
        (unit): PathUnit => ({
          id: str(unit.id),
          code: str(unit.code),
          title: localizedText(unit.title),
          skills: strings(unit.skills),
          lessons: objects(unit.lessons).map(
            (lesson): PathLesson => ({
              id: str(lesson.id),
              code: str(lesson.code),
              title: localizedText(lesson.title),
              state: lessonState(lesson.state),
              score: numOrNull(lesson.score),
              exercises: num(lesson.exercises),
            }),
          ),
        }),
      ),
    }),
  );
  return {
    startLevel: str(data.start_level),
    levels,
    completed: num(data.completed),
    total: num(data.total),
    nextLesson: str(data.next_lesson) || null,
    xp: num(data.xp),
    reviewsDue: num(data.reviews_due),
  };
}

/** Where a lesson is in the path (for its title and « next lesson »). */
export function findLesson(path: CoursePath, id: string | null | undefined): { lesson: PathLesson; unit: PathUnit; level: PathLevel } | null {
  if (!id) return null;
  for (const level of path.levels)
    for (const unit of level.units)
      for (const lesson of unit.lessons) if (lesson.id === id) return { lesson, unit, level };
  return null;
}

export interface VocabularyCard {
  word: string;
  definition: string;
  example: string;
  fr: string;
  /** TCF: the English translation (D-107). */
  en: string;
}

export interface LessonExercise extends Item {
  auto: boolean;
  /** Given when the exercise was already answered: the learner's response and its correction. */
  done: { response: ItemResponse; feedback: Feedback } | null;
}

export interface LessonProgressView {
  status: string;
  score: number;
  bestScore: number;
  attempts: number;
  xpEarned: number;
}

export interface Lesson {
  id: string;
  code: string;
  level: string;
  unit: { code: string; title: LocalizedText };
  title: LocalizedText;
  objectives: string[];
  explanation: LocalizedText;
  vocabulary: VocabularyCard[];
  exercises: LessonExercise[];
  progress: LessonProgressView;
}

export function parseProgressView(value: unknown): LessonProgressView {
  const data = isObject(value) ? value : {};
  return {
    status: str(data.status),
    score: num(data.score),
    bestScore: num(data.best_score),
    attempts: num(data.attempts),
    xpEarned: num(data.xp_earned),
  };
}

export function parseLessonExercise(value: unknown): LessonExercise | null {
  const item = parseItem(value);
  if (!item || !isObject(value)) return null;
  const result = isObject(value.result) ? value.result : null;
  return {
    ...item,
    auto: value.auto !== false && item.type !== "write" && item.type !== "speak",
    done: result ? { response: parseResponse(result.response), feedback: parseFeedback(value) } : null,
  };
}

export function parseLesson(value: unknown): Lesson {
  const data = isObject(value) ? value : {};
  const unit = isObject(data.unit) ? data.unit : {};
  return {
    id: str(data.id),
    code: str(data.code),
    level: str(data.level),
    unit: { code: str(unit.code), title: localizedText(unit.title) },
    title: localizedText(data.title),
    objectives: strings(data.objectives).filter(Boolean),
    explanation: localizedText(data.explanation),
    vocabulary: objects(data.vocabulary)
      .map((v) => ({ word: str(v.word), definition: str(v.definition), example: str(v.example), fr: str(v.fr), en: str(v.en) }))
      .filter((v) => v.word),
    exercises: (Array.isArray(data.exercises) ? data.exercises : [])
      .map(parseLessonExercise)
      .filter((e): e is LessonExercise => e !== null),
    progress: parseProgressView(data.progress),
  };
}

export interface ReviewItem extends Item {
  box: number;
  lapses: number;
}

export function parseReviewToday(value: unknown): { due: number; exercises: ReviewItem[] } {
  const data = isObject(value) ? value : {};
  return {
    due: num(data.due),
    exercises: (Array.isArray(data.exercises) ? data.exercises : [])
      .map((row): ReviewItem | null => {
        const item = parseItem(row);
        return item && isObject(row) ? { ...item, box: num(row.box), lapses: num(row.lapses) } : null;
      })
      .filter((row): row is ReviewItem => row !== null),
  };
}
